import * as React from 'react'
import Head from 'next/head'
import { useSelector, useDispatch } from 'react-redux'
import { END } from 'redux-saga'
import nextCookies from 'next-cookies'
import { AppContext, AppProps } from 'next/app'
import { AppCallback } from 'next-redux-wrapper'
import BaseLayout from '../components/shared/base-layout/BaseLayout'
import ThirdPartyScripts from '../components/shared/third-party-scripts/ThirdPartyScripts'
import GlobalStyle from '../stories/theme/global-styles'
import { flagFeatureEnabled } from '../state/actions/global/global'
import injectClientSideOnlyPolyfills from '../util/inject-client-side-polyfills/injectClientSideOnlyPolyfills'
import handleRouterEvents from '../util/handle-router-events/handleRouterEvents'
import { getConfig } from '../service/util/config/config'
import serverSideRedirectTo from '../util/server-side-redirect-to/serverSideRedirectTo'
import { wrapper, SagaStore } from '../state'
import { SessionProvider, getNextAuthSalesforceCallbackUrl } from '../service/util/oauth/oauth'
import { State } from '../state/reducers'
import { DashboardUser } from '../schema/user'
import { gtmPushVisitorData, gtmPushDtapEnvironment, gtmPushRequestData } from '../util/google-tag-manager-pusher/GoogleTagManagerPusher'

export const App = ({ Component, pageProps, router }: AppProps) => {
  const { DTAP_ENVIRONMENT } = getConfig()
  const user = useSelector<State, DashboardUser>(state => state.user)
  const dispatch = useDispatch()

  React.useEffect(() => {
    gtmPushVisitorData(user)
  }, [user])

  React.useEffect(() => {
    gtmPushDtapEnvironment(DTAP_ENVIRONMENT)

    handleRouterEvents({
      dispatch,
      onComplete: () => {
        gtmPushRequestData(router)
      },
    })

    gtmPushRequestData(router)

    injectClientSideOnlyPolyfills()
  }, [])

  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" type="image/png" href={`${getConfig().ASSET_PREFIX}/images/favicon.png`} />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Montserrat:700,600,500,400|Inter" />
      </Head>
      <GlobalStyle />
      <ThirdPartyScripts />
      <SessionProvider>
        <BaseLayout>
          <Component {...pageProps} />
        </BaseLayout>
      </SessionProvider>
    </>
  )
}

const callback: AppCallback<SagaStore, any> = (store: SagaStore) => async (appContext: AppContext) => {
  const { Component, ctx } = appContext
  const { req, res, query } = ctx

  if (req && res) {
    const cookies = nextCookies(ctx)
    // Set feature flags
    Object.keys(cookies)
      .filter(key => key.startsWith('feature_') && key.length > 'feature_'.length)
      .map(key => key.substring('feature_'.length))
      .forEach(featureName => store.dispatch(flagFeatureEnabled(featureName)))

    store.dispatch(END)
    await store.sagaTask.toPromise()

    if (query.code) {
      // Redirect to next-auth salesforce callback to continue logging in
      serverSideRedirectTo({ res, url: getNextAuthSalesforceCallbackUrl(query) })

      return { pageProps: undefined }
    }
  }

  const pageProps = Component.getInitialProps ? await Component.getInitialProps(ctx) : {}

  return { pageProps }
}

App.getInitialProps = wrapper.getInitialAppProps<any>(callback)

export default wrapper.withRedux(App)
